import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogTitle,
  Button,
  Typography,
  MenuItem,
  Select,
  Input,
  ListItemText,
  CircularProgress,
  InputLabel,
  Tooltip,
} from "@material-ui/core";
import { makeStyles, styled } from "@material-ui/core/styles";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import {
  VERSION_BUILD_STAGE_LABELS,
  countBuildProgress,
  describeExportFailure,
  exportVersion,
  versionSizeEstimate,
} from "../modules/versionPlayEngineApi";
import { formatBytes } from "../modules/playableSizeEstimate";
import BuildWaitLine from "./buildWaitLine";
import { incrementCompanyUsage } from "../modules/companySubscriptionApi";
import { networkViews } from "../modules/playEngineExportTargets";
import { ROOT } from "../modules/pe3dVariantTree";
import DialogDropboxSubmit from "./dialogDropboxSubmit";
import {
  deliversToDropbox,
  dropboxDestination,
  noFolderCancelledMessage,
  resolveExportFileName,
  resolveExportGameTitle,
  usesGameTitleInExportNaming,
} from "../modules/buildProfileUtils";
import _ from "lodash";

import DownloadIcon from "@mui/icons-material/Download";
import ExportPickerList from "./exportPickerList";
import ArrowDropDownRoundedIcon from "@material-ui/icons/ArrowDropDownRounded";

const useStyles = makeStyles(() => ({
  // Two columns, as the pe3d export dialog has: what is being exported on the
  // left, how it is built on the right. One column below ~700px.
  columns: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 380px",
    columnGap: "24px",
    padding: "0 24px 8px 24px",
    alignItems: "stretch",
    // Fills the dialog, which has a height of its own; the two columns then
    // divide that height rather than each standing at its content's size.
    flex: 1,
    minHeight: 0,
    "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)", flex: "none" },
  },
  // `minHeight: 0` or a child with its own scroll refuses to shrink and
  // pushes the column past the dialog instead of scrolling inside it.
  column: { minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflowY: "auto" },
  namingLine: { fontSize: "12px", color: "#8a8d93", lineHeight: 1.6, marginTop: "4px" },
  sizeLine: { fontSize: "14px", fontWeight: 600, color: "#e2e5ea", marginTop: "10px" },
  namingName: { fontFamily: "monospace", color: "#eee", wordBreak: "break-all" },
  footer: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", padding: "4px 24px 20px 24px" },
  // The pe3d export dialog's list, in this dialog's own card.
  listCaption: { display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11px", color: "#8a8d93", letterSpacing: "0.05em", marginBottom: "6px" },
  listCaptionNote: { textTransform: "none", letterSpacing: 0 },
  listLink: { fontSize: "11px", minWidth: 0, padding: "0 4px", marginLeft: "4px", color: "#4eabb1", textTransform: "none" },
  listFilter: { marginBottom: "6px", backgroundColor: "#242424", border: "1px solid #3a3a3a", borderRadius: "4px", padding: "0 8px" },
  versionList: { height: "300px", overflowY: "auto", backgroundColor: "#242424", border: "1px solid #3a3a3a", borderRadius: "4px" },
  listEmpty: { padding: "12px 10px", fontSize: "12px", color: "#8a8d93" },
  chosenCaption: { fontSize: "11px", color: "#8a8d93", letterSpacing: "0.05em", marginTop: "14px", marginBottom: "6px" },
  // Scrolls on its own, so fifty ticked versions cannot push the column past
  // the dialog.
  chosenList: { maxHeight: "160px", overflowY: "auto" },
  chosenName: { fontSize: "12px", color: "#cfd4da", lineHeight: 1.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  // A folder heads the rows beneath it, so it reads larger than them.
  groupRow: { display: "flex", alignItems: "center", gap: "2px", padding: "4px 8px 4px 2px", backgroundColor: "#2a2b30", position: "sticky", top: 0, zIndex: 1, color: "#8a8d93", cursor: "pointer" },
  groupName: { flex: 1, minWidth: 0, fontSize: "14px", fontWeight: 600, color: "#f0f2f4", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  groupCount: { fontSize: "11.5px", fontWeight: 400, color: "#8a8d93" },
  chevron: { flexShrink: 0, opacity: 0.6, fontSize: "11px" },
  versionRow: { display: "flex", alignItems: "center", gap: "2px", paddingRight: "10px", cursor: "pointer", "&:hover": { backgroundColor: "#303030" } },
  versionName: { flex: 1, minWidth: 0, fontSize: "13px", color: "#eee", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  // Flat sections, not stacked boxes: the pe3d export dialog puts its captions
  // and lists straight on the dialog surface, and a fixed-width card inside a
  // two-column grid simply overflows its column.
  downloadDialogCard: {
    margin: "0 0 4px 0",
    padding: 0,
    width: "100%",
    maxWidth: "100%",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: "#eeeeee",
    borderRadius: 0,
    boxShadow: "none",
  },
  versionSelector: {
    width: "100%",
    fontSize: "large",
    fontFamily: "Helvetica",
    color: "#eeeeee",
    "&:before": {
      borderBottom: "1px solid #676767 !important",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottom: "2px solid #ffffff !important",
    },
  },
  inputLabel: { fontSize: "14px", color: "#9bcec0" },
  htmlCreditTypography: { width: "146.6px" },
}));

// `type`, not `mode`: this theme comes from @material-ui/core (v4), where the
// v5 spelling is silently ignored — which left the Select popovers rendering on
// v4's default light palette while the inputs behind them were dark.
const darkTheme = createTheme({
  palette: {
    type: "dark",
    primary: { main: "#4eabb1" },
    // Checkboxes, switches and radios pick `secondary` unless told otherwise,
    // and v4's default for it is pink — which is what a ticked box rendered in.
    secondary: { main: "#4eabb1" },
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  textAlign: "center",
  color: "#eeeeee",
  "& .MuiTypography-h6": {
    fontSize: "20px",
  },
});


// Deliberately smaller and flatter than StyledButton: submitting is the rarer
// action of the two and should not compete with Download for the eye.

const StyledDropDownIcon = styled(ArrowDropDownRoundedIcon)(() => ({
  fontSize: "35px",
}));

/**
 * PlayEngine Export Dialog
 *
 * @param {boolean}  props.isOpen
 * @param {function} props.onClose
 * @param {function} props.setIsDownloadDialogOpen
 * @param {object}   props.selectedVersion
 * @param {object}   props.selectedBase
 * @param {object}   props.selectedGame
 * @param {object}   props.userCompany
 * @param {function} props.setUserCompany
 * @param {function} props.createSnackbar
 * @param {array}    props.plans
 * @param {function} props.getPlanIndex
 * @param {function} props.setIsDownloadingPlayable
 */
// The language select's "All" row; never a language code, so it cannot collide.
const EVERY_LANGUAGE = "__every__";

export default function dialogPlayEngineExport(props) {
  const classes = useStyles();

  const [selectedLanguages, setSelectedLanguages] = useState(["en"]);
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [isBuilding, setIsBuilding] = useState(false);
  const [buttonText, setButtonText] = useState("Download");
  const [error, setError] = useState("");
  const [downloadedHTMLCount, setDownloadedHTMLCount] = useState(0);
  const [ETAMinutes, setETAMinutes] = useState(0);
  const [progressEvents, setProgressEvents] = useState([]);
  const [allLanguages, setAllLanguages] = useState(false);
  const [gameTitleOverride, setGameTitleOverride] = useState("");
  // "current" is the version in the preview; "folder" is the folder it sits in.
  // The networks and languages below are picked once either way and applied to
  // every version in the scope.

  // The same company flag the legacy export reads: bundling is sold per company,
  // not offered to everyone.
  const supportsLangBundle = Boolean(_.get(props, "userCompany.bundleLanguage", false));

  const buildProfileData = props.userCompany?.buildProfileData;
  // Submitting is its own action, confirmed each time: a download can be
  // thrown away, a file in the client's Dropbox cannot.
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  const canDeliverDropbox = deliversToDropbox(buildProfileData);
  const isGameTitleEditable = usesGameTitleInExportNaming(buildProfileData);
  const exportGameTitle = resolveExportGameTitle(buildProfileData, gameTitleOverride, props.selectedGame?.title);

  const supportedLanguages = _.get(props.selectedBase, "supportedLanguages", {});

  /**
   * The base's versions as a checklist, folder by folder — the pe3d export
   * dialog's list (pe3d/dialogPe3dExport.js), which this one follows.
   *
   * The server takes any set of ids on the base (exportVersion.js
   * resolveFolderVersions checks baseID and company), so the picking is not
   * limited to one folder the way the scope radio it replaces was.
   */
  const [ticked, setTicked] = useState([]);
  const [closedGroups, setClosedGroups] = useState([]);
  const [query, setQuery] = useState("");

  /**
   * The base's versions as the shared picker wants them: folders that nest,
   * each with the versions directly in it. The hierarchy is the flat
   * {id, parent, text, droppable} list the version tree is drawn from.
   */
  const pickerTree = useMemo(() => {
    const hierarchy = props.versionHierarchy || [];
    const versionOf = (id) => (props.versions || []).find((version) => String(version._id) === String(id));
    const needle = query.trim().toLowerCase();

    const convert = (parentId) => {
      const children = [];
      const items = [];
      for (const node of hierarchy) {
        if (String(node.parent) !== String(parentId)) continue;
        if (node.droppable === true) {
          const folder = { path: String(node.id), name: node.text, ...convert(node.id) };
          folder.count = folder.items.length + folder.children.reduce((sum, child) => sum + child.count, 0);
          if (folder.count) children.push(folder);
          continue;
        }
        const version = versionOf(node.id);
        if (!version) continue;
        if (needle && !String(version.title || "").toLowerCase().includes(needle)) continue;
        items.push({ id: String(version._id), label: version.title, version });
      }
      return { children, items };
    };

    const root = convert(ROOT);
    root.path = "";
    root.name = "Top level";
    root.count = root.items.length + root.children.reduce((sum, child) => sum + child.count, 0);
    return root;
  }, [props.versionHierarchy, props.versions, query]);

  /** Every version in tree order, UNFILTERED: a search must not drop a tick. */
  const allVersions = useMemo(() => {
    const hierarchy = props.versionHierarchy || [];
    const ids = new Set(hierarchy.filter((node) => node.droppable !== true).map((node) => String(node.id)));
    return (props.versions || []).filter((version) => ids.has(String(version._id)));
  }, [props.versionHierarchy, props.versions]);

  const tickedSet = useMemo(() => new Set(ticked), [ticked]);
  const tickedVersions = useMemo(
    () => allVersions.filter((version) => tickedSet.has(String(version._id))),
    [allVersions, tickedSet]
  );
  // The versions this export covers: what is ticked, or the one on screen
  // before anything is.
  const exportedVersions = tickedVersions.length ? tickedVersions : [props.selectedVersion].filter(Boolean);

  const languagesOfVersion = (version) =>
    _.get(version, "playEngineConfig.localization.availableLanguages", ["en"]) || ["en"];

  // Every language the ticked versions declare between them.
  const availableLanguages = useMemo(
    () => _.uniq(exportedVersions.flatMap(languagesOfVersion)) || ["en"],
    [exportedVersions]
  );

  // A language the ticked versions no longer offer must not stay picked behind
  // the user's back — the server refuses one none of them has.
  const effectiveLanguages = useMemo(() => {
    const kept = selectedLanguages.filter((language) => availableLanguages.includes(language));
    return kept.length ? kept : [availableLanguages[0] || "en"];
  }, [availableLanguages, selectedLanguages]);

  const everyLanguage = availableLanguages.length > 1 && effectiveLanguages.length === availableLanguages.length;

  /**
   * What one version is built in: what was picked, narrowed to what that
   * version carries. The server narrows the same way (containerBuild.js
   * languagesPerVersion), because the engine does not refuse a language a
   * version has no overrides for — it restores the originals, and an English
   * playable would be delivered under another language's name.
   */
  const buildLanguagesOf = (version) => {
    if (allLanguages) return ["all"];
    const own = languagesOfVersion(version);
    const picked = effectiveLanguages.filter((language) => own.includes(language));
    return picked.length ? picked : ["en"];
  };

  /**
   * What the ticked versions weigh once exported.
   *
   * The server runs the engine's own asset rules over a version's config
   * (versionPlayEngine/sizeEstimate), one call per version, kept because the
   * answer is per version and a base can hold hundreds nobody ticked. Cached
   * so re-ticking the same version costs nothing, and a version whose
   * estimate is not in yet is simply left out of the total rather than
   * guessed at.
   */
  const [estimates, setEstimates] = useState({});
  useEffect(() => {
    if (!props.isOpen) return;
    const missing = tickedVersions.map((version) => String(version._id)).filter((id) => !(id in estimates));
    if (!missing.length) return;

    let live = true;
    Promise.all(
      missing.map((id) =>
        versionSizeEstimate(id)
          .then((result) => [id, result.ok ? _.get(result, "data.estimatedExportBytes", null) : null])
          .catch(() => [id, null])
      )
    ).then((pairs) => {
      if (live) setEstimates((current) => ({ ...current, ...Object.fromEntries(pairs) }));
    });
    return () => {
      live = false;
    };
  }, [props.isOpen, tickedVersions]);

  const size = useMemo(() => {
    let bytes = 0;
    let known = 0;
    for (const version of tickedVersions) {
      const own = estimates[String(version._id)];
      if (!own) continue;
      bytes += own * buildLanguagesOf(version).length * selectedNetworks.length;
      known += 1;
    }
    return { bytes, known, unknown: tickedVersions.length - known };
  }, [estimates, tickedVersions, effectiveLanguages, allLanguages, selectedNetworks]);

  const languageBuilds = _.sumBy(exportedVersions, (version) => buildLanguagesOf(version).length) || 1;
  // Ticked versions that will not be built in every language picked.
  const untranslatedVersions = allLanguages
    ? 0
    : exportedVersions.filter((version) => buildLanguagesOf(version).length < effectiveLanguages.length).length;
  // A name for the batch only when every ticked version shares one folder;
  // a selection spanning folders has no single name to give it.
  const tickedFolder = useMemo(() => {
    const holding = [];
    const walk = (node) => {
      if (node.items.some((item) => tickedSet.has(item.id))) holding.push(node);
      node.children.forEach(walk);
    };
    walk(pickerTree);
    return holding.length === 1 && holding[0].name !== "Top level" ? holding[0].name : null;
  }, [pickerTree, tickedSet]);
  const isFolderScope = tickedVersions.length > 1;

  // 'allLanguages' mode counts as 1 language build per network (single HTML with device auto-detect)
  const htmlCount = languageBuilds * selectedNetworks.length;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (props.isOpen) {
      setSelectedLanguages(["en"]);
      setSelectedNetworks([]);
      setAllLanguages(false);
      const current = String(props.selectedVersion?._id || "");
      setTicked(current ? [current] : []);
      // Every folder but the one holding the version on screen, and its
      // parents, starts shut.
      setClosedGroups(() => {
        const open = new Set();
        const walk = (node, trail) => {
          const mine = node.items.some((item) => item.id === current);
          if (mine) trail.forEach((path) => open.add(path));
          node.children.forEach((child) => walk(child, [...trail, child.path]));
        };
        walk(pickerTree, []);
        const shut = [];
        const collect = (node) => {
          node.children.forEach((child) => {
            if (!open.has(child.path)) shut.push(child.path);
            collect(child);
          });
        };
        collect(pickerTree);
        return shut;
      });
      setGameTitleOverride(props.selectedGame?.title || "");
      setIsBuilding(false);
      setButtonText("Download");
      setError("");
      setDownloadedHTMLCount(0);
      setETAMinutes(0);
      setProgressEvents([]);
    }
  }, [props.isOpen]);

  function getLanguageDisplayName(langKey) {
    return supportedLanguages[langKey] || langKey.toUpperCase();
  }

  function handleLanguageChange(event) {
    const value = event.target.value;
    // The "All" row is a toggle, not a language: picking it takes every
    // language on offer, picking it again goes back to one.
    if (value.includes(EVERY_LANGUAGE)) {
      const oneLanguage = availableLanguages.includes("en") ? "en" : availableLanguages[0] || "en";
      setSelectedLanguages(everyLanguage ? [oneLanguage] : availableLanguages);
      return;
    }
    // Any language may be left out, "en" too; unticking the last one keeps it.
    if (!value.length) return;
    setSelectedLanguages(value);
  }

  function handleNetworkChange(event) {
    setSelectedNetworks(event.target.value);
  }

  function getCurrentPlayableCount() {
    const plan = props.plans[props.getPlanIndex(_.get(props, "userCompany.subscription.currentPlan"))];
    const monthlyPlayableCount = _.get(plan, "numberOfPlayables", 0);
    const periods = props.userCompany?.subscription?.periods || [];
    const lastPeriod = periods[periods.length - 1] || {};
    const monthlyCreatedPlayables = lastPeriod.monthlyCreatedPlayables || 0;
    return monthlyPlayableCount - monthlyCreatedPlayables;
  }

  function updatePlayableCounter(totalBuilds, onComplete) {
    if (!(totalBuilds > 0)) {
      onComplete();
      return Promise.resolve();
    }

    return incrementCompanyUsage(_.get(props.userCompany, "_id"), { monthlyCreatedPlayables: totalBuilds })
      .then((newCompany) => props.setUserCompany(newCompany))
      .catch((err) => console.log("createdPlayables update failed: " + (err && err.message)))
      // The build is already delivered; a missed count must not hold it back.
      .then(() => onComplete());
  }

  // ========== EXPORT ==========
  // One request: the engine builds every network × language in the
  // agentContainer and the server composes, zips and uploads (see
  // controller/versionPlayEngine/exportVersion.js). It runs as a job — this
  // polls, and shows each cell of the matrix as it lands.

  function stopBuilding() {
    setIsBuilding(false);
    setButtonText("Download");
    props.setIsDownloadingPlayable(false);
  }

  /**
   * Everything that has to be true before a build starts, reported the way the
   * Download button has always reported it.
   *
   * Shared with Submit so the two refuse for the same reasons at the same
   * moment: asking "send to Dropbox?" about an export that cannot be built is
   * a question nobody can answer usefully.
   */
  function canStartExport() {
    if (selectedNetworks.length === 0) {
      setError("Select at least one network");
      return false;
    }
    if (!allLanguages && effectiveLanguages.length === 0) {
      setError("Select at least one language");
      return false;
    }

    const plan = props.plans[props.getPlanIndex(_.get(props, "userCompany.subscription.currentPlan"))];
    const periods = props.userCompany?.subscription?.periods || [];
    const lastPeriod = periods[periods.length - 1] || {};
    const monthlyCreated = lastPeriod.monthlyCreatedPlayables || 0;
    const monthlyLimit = _.get(plan, "numberOfPlayables", 0);
    const builds = htmlCount;

    if (monthlyCreated + builds > monthlyLimit) {
      props.createSnackbar(`Your company's current plan allows up to ${monthlyLimit} playables`, "warning");
      return false;
    }
    return true;
  }

  async function downloadPlayEngine(toDropbox = false, dropboxFolder = "") {
    if (!canStartExport()) return;

    const totalBuilds = htmlCount;

    setError("");
    setIsBuilding(true);
    setButtonText("Building...");
    setDownloadedHTMLCount(0);
    setProgressEvents([]);
    setETAMinutes(Math.ceil((totalBuilds * 12) / 60));
    props.setIsDownloadingPlayable(true);

    const result = await exportVersion(
      {
        baseID: props.selectedBase._id,
        versionID: props.selectedVersion._id,
        networks: selectedNetworks,
        languages: allLanguages ? ["all"] : effectiveLanguages,
        ...(isFolderScope
          ? {
            versionIDs: tickedVersions.map((version) => version._id),
            ...(tickedFolder ? { folderName: tickedFolder } : {}),
          }
          : {}),
        gameTitle: exportGameTitle,
        deliverToDropbox: toDropbox,
        ...(dropboxFolder ? { dropboxFolder } : {}),
      },
      {
        onProgress: (progress) => {
          const { built, failed } = countBuildProgress(progress);
          setProgressEvents(progress);
          const done = built + failed;
          setDownloadedHTMLCount(done);
          const minutes = Math.ceil(((totalBuilds - done) * 12) / 60);
          setETAMinutes(minutes > 0 ? minutes : 1);
        },
      }
    );

    if (!result.ok || !_.has(result, "data.file")) {
      setError(describeExportFailure(result));
      stopBuilding();
      return;
    }

    const message = noFolderCancelledMessage(result.data.noFolderCancelledNetworks || []);
    if (message) props.createSnackbar?.(message, "warning", false);
    // The delivery reports itself through the export's warnings, so it reaches
    // the person even though this dialog closes a few lines down.
    for (const line of result.data.warnings || []) {
      if (String(line).startsWith("Dropbox")) props.createSnackbar?.(line, "info", false);
    }
    if (result.data.failed?.length) {
      props.createSnackbar?.(
        `${result.data.failed.length} of ${totalBuilds} builds failed and are missing from the zip`,
        "warning",
        false
      );
    }

    stopBuilding();
    props.setIsDownloadDialogOpen(false);
    updatePlayableCounter(totalBuilds, () => {
      // A submitted export went to Dropbox; nothing should land in the
      // person's Downloads folder as well.
      if (!toDropbox) window.location = result.data.file;
    });
  }

  const creditLeft = getCurrentPlayableCount() - htmlCount;

  /**
   * The name each ticked version lands under, in the order of the list.
   *
   * One example used to stand for all of them, which read as if the whole
   * export were called that. Every version resolves the same template with
   * its own title, so the list says so version by version.
   */
  const exportFileNames = useMemo(
    () =>
      (tickedVersions.length ? tickedVersions : [props.selectedVersion].filter(Boolean)).map((version) =>
        resolveExportFileName(buildProfileData, {
          languages: allLanguages ? ["ML"] : buildLanguagesOf(version),
          adNetworks: selectedNetworks,
          versionName: version.title || "",
          gameTitle: exportGameTitle || "",
          baseTitle: props.selectedBase?.title || "",
          timeStamp: "{timeStamp}",
        })
      ),
    [buildProfileData, allLanguages, effectiveLanguages, selectedNetworks, tickedVersions, props.selectedVersion, exportGameTitle, props.selectedBase]
  );

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      maxWidth={false}
      PaperProps={{
        style: {
          borderRadius: "10px",
          backgroundColor: "#2c2c2c",
          color: "#eeeeee",
          width: 960,
          maxWidth: "calc(100vw - 64px)",
          // A fixed height rather than one the content decides, as the other
          // two export dialogs have: the right-hand column is what makes this
          // tall, and the list fills whatever that comes to.
          height: "min(760px, calc(100vh - 64px))",
        },
      }}
    >
      <StyledDialogTitle>EXPORT PLAYENGINE</StyledDialogTitle>

      <div className={classes.columns}>
        <div className={classes.column}>
          {/* Which versions: the same checklist the pe3d and snapshot export
              dialogs use, so all three pick the same way. */}
          <ExportPickerList
            caption="VERSIONS"
            tree={pickerTree}
            total={allVersions.length}
            tickedIds={ticked}
            onTicked={setTicked}
            query={query}
            onQuery={setQuery}
            searchPlaceholder="Search versions"
            closedPaths={closedGroups}
            onToggleFolder={(path) =>
              setClosedGroups((current) => (current.includes(path) ? current.filter((item) => item !== path) : [...current, path]))
            }
            disabled={isBuilding}
            emptyText={allVersions.length === 0 ? "This base has no versions yet." : "No version matches."}
          />

        </div>
        <div className={classes.column}>
          {isGameTitleEditable && (
            <Card className={classes.downloadDialogCard}>
              <ThemeProvider theme={darkTheme}>
                <CardContent style={{ padding: "4px 0 0 0" }}>
                  <InputLabel className={classes.inputLabel}>Game Title</InputLabel>
                  <Input
                    className={classes.versionSelector}
                    value={gameTitleOverride}
                    placeholder={props.selectedGame?.title}
                    onChange={(event) => setGameTitleOverride(event.target.value)}
                  />
                </CardContent>
              </ThemeProvider>
            </Card>
          )}

          {/* Language Selection */}
          <Card className={classes.downloadDialogCard}>
            <ThemeProvider theme={darkTheme}>
              <CardContent style={{ padding: "4px 0 0 0" }}>
                <InputLabel className={classes.inputLabel}>Select Language</InputLabel>
                <Select
                  multiple
                  disabled={allLanguages}
                  IconComponent={StyledDropDownIcon}
                  input={<Input />}
                  className={classes.versionSelector}
                  renderValue={() => {
                    if (allLanguages) return "All languages in one HTML";
                    if (everyLanguage) return "All";
                    if (effectiveLanguages.length === 1) {
                      return getLanguageDisplayName(effectiveLanguages[0]);
                    }
                    return effectiveLanguages.map((l) => getLanguageDisplayName(l)).join(", ");
                  }}
                  onChange={handleLanguageChange}
                  value={effectiveLanguages}
                >
                  {availableLanguages.length > 1 && (
                    <MenuItem value={EVERY_LANGUAGE}>
                      <Checkbox checked={everyLanguage} />
                      <ListItemText primary="All" />
                    </MenuItem>
                  )}
                  {availableLanguages.map((langKey) => (
                    <MenuItem key={langKey} value={langKey}>
                      <Checkbox checked={effectiveLanguages.indexOf(langKey) > -1} />
                      <ListItemText primary={getLanguageDisplayName(langKey)} />
                    </MenuItem>
                  ))}
                </Select>
                {supportsLangBundle && (
                  <div
                    style={{ display: "flex", alignItems: "center", marginTop: "8px", cursor: "pointer" }}
                    onClick={() => setAllLanguages((v) => !v)}
                  >
                    <Checkbox checked={allLanguages} style={{ padding: "2px 6px 2px 0" }} />
                    <Tooltip title="Embed every edited language into a single HTML and auto-select by device language at runtime.">
                      <Typography style={{ color: "#9bcec0", fontSize: "14px" }}>
                    Bundle all languages into one HTML
                      </Typography>
                    </Tooltip>
                  </div>
                )}
              </CardContent>
            </ThemeProvider>
          </Card>

          {/* Network Selection */}
          <Card className={classes.downloadDialogCard}>
            <ThemeProvider theme={darkTheme}>
              <CardContent style={{ padding: "4px 0 0 0" }}>
                <InputLabel className={classes.inputLabel}>Select Network</InputLabel>
                <Select
                  multiple
                  IconComponent={StyledDropDownIcon}
                  input={<Input />}
                  className={classes.versionSelector}
                  renderValue={() => {
                    if (selectedNetworks.length === 0) return "Select Network";
                    if (selectedNetworks.length === 1) {
                      return networkViews.find((n) => n.name === selectedNetworks[0])?.caption;
                    }
                    return selectedNetworks
                      .map((n) => networkViews.find((nv) => nv.name === n)?.caption)
                      .join(", ");
                  }}
                  onChange={handleNetworkChange}
                  value={selectedNetworks}
                >
                  {networkViews.map((network) => (
                    <MenuItem key={network.name} value={network.name}>
                      <Checkbox checked={selectedNetworks.indexOf(network.name) > -1} />
                      <ListItemText primary={network.caption} />
                    </MenuItem>
                  ))}
                </Select>
              </CardContent>
            </ThemeProvider>
          </Card>

          {/* Neither the loading screen nor font shrinking is offered here. The
          screen is one setting per company (superAdmin → Companies & bases →
          Settings); shrinking is always on — it renders identically and only
          makes the playable smaller, so there is nothing to decide. */}

          {/* Credits */}
          <Card className={classes.downloadDialogCard}>
            <CardContent
              style={{
                textAlign: "center",
                margin: 0,
                padding: 5,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                lineHeight: "0.75",
              }}
            >
              <div className={classes.htmlCreditTypography}>
                <Typography style={{ textAlign: "left", marginLeft: "10px", color: "#9bcec0" }}>
              Current Credit
                </Typography>
              </div>
              <div className={classes.htmlCreditTypography}>
                <Typography style={{ textAlign: "center", color: "#9bcec0" }}>Html Count</Typography>
              </div>
              <div className={classes.htmlCreditTypography}>
                <Typography style={{ textAlign: "right", marginRight: "10px", color: "#9bcec0" }}>
              Credit Left
                </Typography>
              </div>
            </CardContent>
            <CardContent
              style={{
                textAlign: "center",
                margin: 0,
                marginTop: "-10px",
                display: "flex",
                padding: 5,
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                lineHeight: "0.75",
              }}
            >
              <div className={classes.htmlCreditTypography}>
                <Typography style={{ textAlign: "left", marginLeft: "10px" }}>
                  {getCurrentPlayableCount()}
                </Typography>
              </div>
              <div className={classes.htmlCreditTypography}>
                <Typography style={{ textAlign: "center" }}>{htmlCount}</Typography>
              </div>
              <div className={classes.htmlCreditTypography}>
                <Typography style={{ textAlign: "right", marginRight: "10px" }}>{creditLeft}</Typography>
              </div>
            </CardContent>
          </Card>

          {tickedVersions.length > 0 && (
            <>
              <div className={classes.chosenCaption}>GOING OUT</div>
              <div className={classes.chosenList}>
                {tickedVersions.map((version, index) => (
                  <div key={String(version._id)} className={classes.chosenName} title={version.title}>
                    {exportFileNames[index] || version.title}
                  </div>
                ))}
              </div>
            </>
          )}

          {size.bytes > 0 && (
            <Typography className={classes.sizeLine}>
              {`About ~${formatBytes(size.bytes)}${htmlCount > 1 ? " across all builds" : ""}.`}
              {size.unknown > 0 ? ` ${size.unknown} not estimated yet.` : ""}
            </Typography>
          )}

          {untranslatedVersions > 0 && !allLanguages && (
            <Typography className={classes.namingLine}>
              {`${untranslatedVersions} of the ticked versions are not translated into every language picked; each is built only in the ones it has.`}
            </Typography>
          )}

          {exportFileNames.some(Boolean) && (
            <Typography className={classes.namingLine}>Named by the company&apos;s convention.</Typography>
          )}
        </div>
      </div>

      {/* Progress */}
      {isBuilding && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            marginBottom: "15px",
          }}
        >
          <CircularProgress color="secondary" style={{ marginBottom: "10px" }} />
          <Typography style={{ whiteSpace: "pre-line", textAlign: "center" }}>
            <BuildWaitLine
              events={progressEvents}
              active={isBuilding}
              stageLabels={VERSION_BUILD_STAGE_LABELS}
              style={{ display: "block" }}
            />
            {`HTML built: ${downloadedHTMLCount}/${htmlCount}\nETA: ${ETAMinutes} minute(s)`}
          </Typography>
        </div>
      )}

      {/* The footer the pe3d export dialog wears: a quiet Close, then the one
          contained verb, with Dropbox beside it when the profile delivers there. */}
      {!isBuilding && (
        <div className={classes.footer}>
          <Button onClick={props.onClose} style={{ color: "#bbb" }}>
            Close
          </Button>
          {canDeliverDropbox && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => canStartExport() && setIsSubmitConfirmOpen(true)}
              disabled={isBuilding}
              style={{ color: "#9bcec0", borderColor: "#4a6d68", textTransform: "none" }}
            >
              Submit to Dropbox
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => downloadPlayEngine(false)}
            disabled={isBuilding}
          >
            <DownloadIcon style={{ width: 18, height: 18, marginRight: 6 }} />
            {buttonText}
          </Button>
        </div>
      )}

      {/* Error */}
      {error && (
        <Typography style={{ textAlign: "center", color: "#ff6b6b", marginBottom: "15px" }}>
          {error}
        </Typography>
      )}
      <DialogDropboxSubmit
        isOpen={isSubmitConfirmOpen}
        defaultCompanyFolder={props.userCompany?.name}
        defaultGameFolder={props.selectedGame?.title}
        gameCode={exportGameTitle}
        destinationFor={(folder) =>
          dropboxDestination(buildProfileData, {
            dropboxFolder: folder,
            gameTitle: exportGameTitle,
            versionName: props.selectedVersion?.title?.replace(/ /g, "_"),
          })
        }
        onCancel={() => setIsSubmitConfirmOpen(false)}
        onConfirm={(folder) => {
          setIsSubmitConfirmOpen(false);
          downloadPlayEngine(true, folder);
        }}
      />
    </Dialog>
  );
}
